<script setup>
import ButtonDs from "./ButtonDs.vue";
// Astra DS · Door — la puerta.
//
// Figma: Pattern/Door 712:544. En la plantilla, tres de 480 en fila
// (Template/Home-Reset 730:7237 › puertas 730:7272).
//
// «Puerta: lleva a otra sección de la casa. Van en fila de tres bajo el hero —
// TV, nuevas realizaciones y demos. La etiqueta nombra el destino; el pie dice
// qué te vas a encontrar.»
//
// El arte va en su propia capa y no en el contenedor: filtrar el contenedor
// teñiría también la etiqueta y el control de play. Eso ya lo resuelve
// DuotoneArt, así que aquí no se vuelve a escribir.
//
// El slot `art` existe para la puerta de lanzamientos, que en vez de una
// imagen lleva tres pasando solas. El resto usa el hueco por defecto.
import { computed } from "vue";
import DuotoneArt from "./DuotoneArt.vue";

const props = defineProps({
  // Etiqueta de esquina: nombra el destino («Último en TV»).
  badge: { type: String, default: "" },
  title: { type: String, required: true },
  // Las celdas del pie, separadas por filete. No un string con separadores:
  // el filete es un borde, no un carácter.
  meta: { type: Array, default: () => [] },
  cta: { type: String, default: "" },

  image: { type: String, default: "" },
  // A qué volver si `image` no carga —o carga vacía—. Lo pide la puerta de
  // TV, cuya miniatura viene de YouTube y puede no existir.
  imageFallback: { type: String, default: "" },
  imageAlt: { type: String, default: "" },

  // Solo la puerta de TV. Va abajo-izquierda, no centrado.
  showPlay: { type: Boolean, default: false },

  to: { type: [String, Object], default: null },
  href: { type: String, default: "" },
});

defineEmits(["play"]);

const tag = computed(() => {
  if (props.to) return "router-link";
  if (props.href) return "a";
  return "button";
});

// Pasar `href` a un <router-link> le sobrescribe el href generado. Misma
// trampa que en ChipAction: solo se enlaza la prop del tag que toca.
const linkProps = computed(() => {
  if (props.to) return { to: props.to };
  if (props.href) return { href: props.href };
  return { type: "button" };
});
</script>

<template>
  <component :is="tag" class="door" v-bind="linkProps">
    <div class="door__art">
      <slot name="art">
        <DuotoneArt
          :src="image"
          :fallback="imageFallback"
          :alt="imageAlt"
          :tag="badge"
          tag-anchor="top"
          :show-play="showPlay"
          play-anchor="bottom-left"
          ratio="auto"
          @play="$emit('play')"
        />
      </slot>
    </div>

    <div class="door__body">
      <h2 class="door__title">{{ title }}</h2>

      <p v-if="meta.length" class="door__meta">
        <span v-for="cell in meta" :key="cell">{{ cell }}</span>
      </p>

      <!-- La llamada de la puerta es el botón del sistema en su forma
           discreta. Va en `span` porque la puerta entera ya es el enlace: un
           enlace dentro de otro no es marcado válido. -->
      <ButtonDs v-if="cta" class="door__cta" :label="cta" variant="quiet" size="micro" as="span" />
    </div>
  </component>
</template>

<style scoped lang="scss">
@use "@/assets/scss/interaccion" as i;
@use "@/assets/scss/type" as t;

.door {
  // Arte a color y llamada con el filete marcado: el patrón de tarjeta de la
  // casa, que nació aquí y ahora vive en `scss/_interaccion.scss`.
  @include i.tarjeta;

  display: flex;
  flex-direction: column;
  // 🔴 `stretch` explícito, aunque sea el valor por defecto de flex.
  //
  // No lo es para un `<button>`: Chrome le pone `align-items: center` en su
  // hoja de usuario, y la puerta de TV es la única que se renderiza como botón
  // —no navega, reproduce—. Resultado: su cuerpo no se estiraba, tomaba el
  // ancho del texto y quedaba centrado, con 70px muertos a cada lado. Las otras
  // dos puertas son enlaces y por eso salían bien: el mismo componente, dos
  // etiquetas y dos comportamientos.
  align-items: stretch;
  box-sizing: border-box;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: var(--color-bg-canvas);
  color: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
}

// **16:9.** Era altura fija de 250, la del nodo, y con ella el hueco quedaba
// en 1,92:1 sobre unas imágenes que llegan a 4:3 desde `actualidad.json`: se
// perdía el 31% por abajo, que es donde la redacción pone el nombre del grupo
// en la portada del disco. A 16:9 el recorte baja a un 25% y la fila de
// puertas apenas se mueve —de 395 a ~414—, que era la condición: las tres
// comparten altura y llevarlas a 4:3 estiraba la portada entera.
//
// El cuerpo sigue creciendo por debajo; el arte no.
.door__art {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
}

// El hueco tiene alto propio, así que la relación de aspecto sobra: sin esto
// DuotoneArt impone un cuadrado y la imagen se corta a 480×480.
.door__art :deep(.duotone) {
  height: 100%;
}

.door__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 9px;
  padding: 22px 24px;
  min-width: 0;
}

.door__title {
  @include t.display-item;
  margin: 0;
  color: var(--color-text-primary);
}

// El filete entre celdas es un borde, y la última no lo lleva. Con un
// separador escrito quedaría uno colgando al final de la fila.
.door__meta {
  display: flex;
  flex-wrap: wrap;
  margin: 0;

  span {
    @include t.mono-nano;
    margin-right: 10px;
    padding-right: 10px;
    border-right: 1px solid var(--color-border-default);
    color: var(--color-text-secondary);

    &:last-child {
      margin-right: 0;
      padding-right: 0;
      border-right: 0;
    }
  }
}

// Pegado al pie de la puerta, no al texto: las tres puertas tienen cuerpos de
// distinto largo y sus llamadas tienen que alinear entre sí.
.door__cta {
  align-self: flex-start;
  margin-top: auto;
}

.door:focus-visible {
  outline: 2px solid var(--color-border-focus);
  outline-offset: -2px;
}

@media (prefers-reduced-motion: reduce) {
  .door__cta {
    transition: none;
  }
}

// ── El margen móvil ──
//
// 20 a cada lado, como el resto del sistema por debajo de 880. Este bloque se
// había quedado con el de escritorio, y en una pantalla de 390 convivían tres
// verticales distintas —20, 24 y 28— según de qué componente fuera cada caja.
// Se lee como desalineación aunque cada bloque, por separado, esté bien.
//
// Mismo corte que ModuleTitleBar, que era el único que ya lo hacía.
@media (max-width: 880px) {
  .door__body {
    padding: 20px;
  }
}
</style>
